import { faArrowLeft, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getGroups, getMyLink } from "../../api/Links";
import { createVibe } from "../../api/categories";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Loader from "../../components/Loader";
import ConfirmDialog from "../../components/ConfirmDialog";

const Step2LinkVibe = ({ formData, setFormData, goNext, goBack }) => {
  const { t } = useTranslation();
  const [showNoMembersModal, setShowNoMembersModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [individualMembers, setIndividualMembers] = useState(
    formData.members || []
  );
  const [groupMembers, setGroupMembers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);

  const [groups, setGroups] = useState([]);
  const [activeTab, setActiveTab] = useState(t("Individual"));

  useEffect(() => {
    let updatedMembers = individualMembers;
    let updatedGroups = selectedGroups;
    let updatedFormData = { ...formData };

    // From location.state.members (when coming back from AddIndividual)
    if (location.state?.members && Array.isArray(location.state.members)) {
      updatedMembers = location.state.members;
      updatedFormData = {
        ...updatedFormData,
        ...location.state.formData,
        members: location.state.members,
      };
    } else if (formData?.members) {
      updatedMembers = formData.members;
    }

    // From formData.vibe_links or location.state.selectedGroups
    if (formData?.vibe_links && formData.vibe_links.length > 0) {
      updatedGroups = formData.vibe_links.map((g) => g.id);
    } else if (
      location.state?.selectedGroups &&
      location.state.selectedGroups.length > 0
    ) {
      updatedGroups = location.state.selectedGroups;
      updatedFormData = {
        ...updatedFormData,
        vibe_links: location.state.selectedGroups.map((id) => {
          const g = groups.find((gr) => gr.id === id);
          return g ? { id: g.id, name: g.name, count: g.count } : { id };
        }),
      };
    }

    // Apply updates
    setIndividualMembers(updatedMembers);
    setSelectedGroups(updatedGroups);
    setFormData(updatedFormData);
  }, [location.state, formData?.vibe_links, groups]);

  // ✅ Fetch groups
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await getGroups();
        setGroups(res.data || []);
      } catch (err) {
        setGroups([]);
      }
    };
    fetchGroups();
  }, []);

  // ✅ Fetch group members when activeTab changes
  useEffect(() => {
    if (activeTab !== t("Individual")) {
      const fetchMembers = async () => {
        try {
          const groupObj = groups.find((g) => g.name === activeTab);
          if (!groupObj) return setGroupMembers([]);
          const res = await getMyLink(groupObj.id);
          let membersArr = [];
          if (Array.isArray(res)) {
            membersArr = res;
          } else if (Array.isArray(res.data)) {
            membersArr = res.data;
          } else if (Array.isArray(res.members)) {
            membersArr = res.members;
          }
          setGroupMembers(membersArr);
        } catch (err) {
          setGroupMembers([]);
        }
      };
      fetchMembers();
    }
  }, [activeTab, groups]);

  // ✅ Handle tab click
  // ✅ Handle tab click
  const handleTabClick = (tab) => {
    if (tab === t("Individual")) {
      // Ensure formData has up-to-date vibe_links before navigating
      const updatedFormData = {
        ...formData,
        vibe_links: selectedGroups.map((id) => {
          const g = groups.find((gr) => gr.id === id);
          return g ? { id: g.id, name: g.name, count: g.count } : { id };
        }),
      };

      navigate("/add-individual", {
        state: {
          members: individualMembers,
          formData: updatedFormData,
          selectedGroups,
        },
      });
      setFormData(updatedFormData);
      setActiveTab(tab);
    } else {
      setActiveTab(tab);
      const groupObj = groups.find((g) => g.name === tab);
      if (!groupObj) return;
      if (!groupObj.count || groupObj.count === 0) {
        setShowNoMembersModal(true);
        return;
      }
      setSelectedGroups((prev) => {
        if (prev.includes(groupObj.id)) {
          return prev.filter((id) => id !== groupObj.id);
        } else {
          return [...prev, groupObj.id];
        }
      });
    }
  };

  // ✅ Save handler
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const handleSave = async () => {
    if (individualMembers.length === 0 && selectedGroups.length === 0) {
      toast.error(t("please_select_links_or_individual_member"));
      return;
    }
    if (!formData.id || !formData.vibe_category_id) {
      toast.error(t("please_complete_all_required_fields_before_saving"));
      navigate("/create-vibe");
      return;
    }

    // check for empty groups
    if (selectedGroups.length > 0) {
      for (const groupId of selectedGroups) {
        const groupObj = groups.find((g) => g.id === groupId);
        if (groupObj && (!groupObj.count || groupObj.count === 0)) {
          toast.error(
            t(
              "The_selected_group_has_no_members_Please_add_members_to_this_group_before_proceeding."
            )
          );
          setShowNoMembersModal(true);
          return;
        }
      }
    }

    setSaving(true);
    setSaveError("");

    let payload = {
      id: formData.id,
      vibe_category_id: formData.vibe_category_id,
      steps: 2,
    };

    if (individualMembers.length > 0) {
      payload.individual_members = JSON.stringify(individualMembers);
    }

    if (selectedGroups.length > 0) {
      payload.vibe_links = JSON.stringify(selectedGroups);
    }

    try {
      const res = await createVibe(payload);
      if (!res || (res.error && res.error !== "")) {
        setSaveError(res?.error || "Unknown error occurred while saving.");
        return;
      }

      setFormData((prev) => ({
        ...prev,
        vibe_links: selectedGroups
          .map((id) => {
            const g = groups.find((gr) => gr.id === id);
            return g ? { id: g.id, name: g.name, count: g.count } : null;
          })
          .filter(Boolean),
        members: individualMembers,
        individual_members: individualMembers,
        distance: "",
        category: "",
      }));

      if (location.state && location.state.members) {
        location.state.members = [];
      }
      goNext();
    } catch (err) {
      setSaveError(err?.message || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ✅ Count for badge
  const getCount = (tab) => {
    if (tab === t("Individual")) return individualMembers.length;
    const groupObj = groups.find((g) => g.name === tab);
    return groupObj ? groupObj.count || 0 : 0;
  };

  const sortedGroups = [
    ...groups.filter((g) => g.default),
    ...groups.filter((g) => !g.default),
  ];

  const tabs = [t("Individual"), ...sortedGroups.map((g) => g.name)];

  return (
    <>
      {/* No Members Modal */}
      <ConfirmDialog
        open={showNoMembersModal}
        title={t("no_members_in_group")}
        message={t("group_has_no_members_message")}
        onConfirm={() => {
          setShowNoMembersModal(false);
          const groupObj = groups.find((g) => g.name === activeTab);
          if (groupObj) {
            navigate("/my-links", {
              state: { selectedGroupId: groupObj.id },
            });
          } else {
            navigate("/my-links");
          }
        }}
        onCancel={() => {
          // toggle();
          setShowNoMembersModal(false);
        }}
        yesLabel={t("ok")}
        noLabel={t("cancel")}
      />

      {/* Header */}
      {saveError && (
        <div className="mb-4 p-2 bg-red-100 text-red-700 rounded text-center">
          {saveError}
        </div>
      )}
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={goBack}
          className=" py-2 rounded-lg font-semibold text-gray-700"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h1
          className="text-md sm:text-md md:text-lg font-bold "
          style={{ color: "#6d2af6" }}
        >
          {t("vibe", "Vibe")}
        </h1>
        <button
          className="text-sm px-4 py-2 rounded-lg font-semibold bg-[#6d29f6] text-white cursor-pointer flex items-center justify-center whitespace-nowrap min-w-[120px]"
          style={
            individualMembers.length > 0 || selectedGroups.length > 0
              ? { color: "#fff" }
              : {}
          }
          onClick={handleSave}
        >
          {saving ? (
            <Loader loaderColor="white" size={20} />
          ) : (
            t("save_next", "Save & Next")
          )}
        </button>
      </div>

      <h1 className="flex w-full justify-center py-3 text-black dark:text-white text-md sm:text-md md:text-lg font-bold ">
        {t("link_your_vibe", "Link Your Vibe")}
      </h1>
      {/* Tabs */}
      <div className="flex flex-col gap-4 mb-6">
      {tabs.map((tab) => {
  const groupObj = groups.find((g) => g.name === tab);
  const isSelected = groupObj ? selectedGroups.includes(groupObj.id) : false;
  const isDefault = groupObj?.default === true;
  const hasIndividualMembers = individualMembers.length > 0;

  // Determine background color
  const bgColor =
    tab === t("Individual")
      ? "purple"
      : isDefault
      ? "purple"
      : isSelected
      ? "purple"
      : "white";

  // Determine text color
  const textColor =
    bgColor === "white"
      ? "!text-black dark:!text-black"
      : "text-white";

  // Determine border color
  let borderColor = "border-[#6D29F6]"; // default purple

  if (tab === t("Individual") && hasIndividualMembers) {
    borderColor = "border-green-500";
  } else if (isDefault && isSelected) {
    borderColor = "border-green-500";
  }

  return (
    <div
      key={tab}
      onClick={() => handleTabClick(tab)}
      className={`relative p-2 rounded-lg text-center font-semibold cursor-pointer transition border-2
        ${
          bgColor === "purple"
            ? "bg-[#6d29f6]"
            : "bg-white dark:bg-white"
        }
        ${borderColor}
      `}
    >
      <div className={`flex items-center justify-center gap-2 ${textColor}`}>
        {tab === t("Individual") && <FontAwesomeIcon icon={faPlus} />}
        {tab}
      </div>

      <span
        className="text-xs absolute right-[-10px] top-[-10px] bg-[#6d29f6] text-white text-sm w-6 h-6 flex items-center justify-center rounded-full border-2 border-white shadow"
        style={{ fontWeight: "bold" }}
      >
        {getCount(tab)}
      </span>
    </div>
  );
})}
 

      </div>
    </>
  );
};

export default Step2LinkVibe;
